/**
 * This code was AUTOGENERATED using the Codama library.
 * Please DO NOT EDIT THIS FILE, instead use visitors
 * to add features, then rerun Codama to update it.
 *
 * @see https://github.com/codama-idl/codama
 */

import {
  assertAccountExists,
  assertAccountsExist,
  combineCodec,
  decodeAccount,
  fetchEncodedAccount,
  fetchEncodedAccounts,
  fixDecoderSize,
  fixEncoderSize,
  getAddressDecoder,
  getAddressEncoder,
  getBooleanDecoder,
  getBooleanEncoder,
  getBytesDecoder,
  getBytesEncoder,
  getStructDecoder,
  getStructEncoder,
  getU64Decoder,
  getU64Encoder,
  transformEncoder,
  type Account,
  type Address,
  type EncodedAccount,
  type FetchAccountConfig,
  type FetchAccountsConfig,
  type FixedSizeCodec,
  type FixedSizeDecoder,
  type FixedSizeEncoder,
  type MaybeAccount,
  type MaybeEncodedAccount,
  type ReadonlyUint8Array,
} from '@solana/kit';
import { findTreeConfigPda, TreeConfigSeeds } from '../pdas';
import {
  getDecompressibleStateDecoder,
  getDecompressibleStateEncoder,
  getVersionDecoder,
  getVersionEncoder,
  type DecompressibleState,
  type DecompressibleStateArgs,
  type Version,
  type VersionArgs,
} from '../types';

export const TREE_CONFIG_DISCRIMINATOR = new Uint8Array([
  122, 245, 175, 248, 171, 34, 0, 207,
]);

export function getTreeConfigDiscriminatorBytes() {
  return fixEncoderSize(getBytesEncoder(), 8).encode(TREE_CONFIG_DISCRIMINATOR);
}

export type TreeConfig = {
  discriminator: ReadonlyUint8Array;
  treeCreator: Address;
  treeDelegate: Address;
  totalMintCapacity: bigint;
  numMinted: bigint;
  isPublic: boolean;
  isDecompressible: DecompressibleState;
  version: Version;
};

export type TreeConfigArgs = {
  treeCreator: Address;
  treeDelegate: Address;
  totalMintCapacity: number | bigint;
  numMinted: number | bigint;
  isPublic: boolean;
  isDecompressible: DecompressibleStateArgs;
  version: VersionArgs;
};

export function getTreeConfigEncoder(): FixedSizeEncoder<TreeConfigArgs> {
  return transformEncoder(
    getStructEncoder([
      ['discriminator', fixEncoderSize(getBytesEncoder(), 8)],
      ['treeCreator', getAddressEncoder()],
      ['treeDelegate', getAddressEncoder()],
      ['totalMintCapacity', getU64Encoder()],
      ['numMinted', getU64Encoder()],
      ['isPublic', getBooleanEncoder()],
      ['isDecompressible', getDecompressibleStateEncoder()],
      ['version', getVersionEncoder()],
    ]),
    (value) => ({ ...value, discriminator: TREE_CONFIG_DISCRIMINATOR })
  );
}

export function getTreeConfigDecoder(): FixedSizeDecoder<TreeConfig> {
  return getStructDecoder([
    ['discriminator', fixDecoderSize(getBytesDecoder(), 8)],
    ['treeCreator', getAddressDecoder()],
    ['treeDelegate', getAddressDecoder()],
    ['totalMintCapacity', getU64Decoder()],
    ['numMinted', getU64Decoder()],
    ['isPublic', getBooleanDecoder()],
    ['isDecompressible', getDecompressibleStateDecoder()],
    ['version', getVersionDecoder()],
  ]);
}

export function getTreeConfigCodec(): FixedSizeCodec<
  TreeConfigArgs,
  TreeConfig
> {
  return combineCodec(getTreeConfigEncoder(), getTreeConfigDecoder());
}

export function decodeTreeConfig<TAddress extends string = string>(
  encodedAccount: EncodedAccount<TAddress>
): Account<TreeConfig, TAddress>;
export function decodeTreeConfig<TAddress extends string = string>(
  encodedAccount: MaybeEncodedAccount<TAddress>
): MaybeAccount<TreeConfig, TAddress>;
export function decodeTreeConfig<TAddress extends string = string>(
  encodedAccount: EncodedAccount<TAddress> | MaybeEncodedAccount<TAddress>
): Account<TreeConfig, TAddress> | MaybeAccount<TreeConfig, TAddress> {
  return decodeAccount(
    encodedAccount as MaybeEncodedAccount<TAddress>,
    getTreeConfigDecoder()
  );
}

export async function fetchTreeConfig<TAddress extends string = string>(
  rpc: Parameters<typeof fetchEncodedAccount>[0],
  address: Address<TAddress>,
  config?: FetchAccountConfig
): Promise<Account<TreeConfig, TAddress>> {
  const maybeAccount = await fetchMaybeTreeConfig(rpc, address, config);
  assertAccountExists(maybeAccount);
  return maybeAccount;
}

export async function fetchMaybeTreeConfig<TAddress extends string = string>(
  rpc: Parameters<typeof fetchEncodedAccount>[0],
  address: Address<TAddress>,
  config?: FetchAccountConfig
): Promise<MaybeAccount<TreeConfig, TAddress>> {
  const maybeAccount = await fetchEncodedAccount(rpc, address, config);
  return decodeTreeConfig(maybeAccount);
}

export async function fetchAllTreeConfig(
  rpc: Parameters<typeof fetchEncodedAccounts>[0],
  addresses: Array<Address>,
  config?: FetchAccountsConfig
): Promise<Account<TreeConfig>[]> {
  const maybeAccounts = await fetchAllMaybeTreeConfig(rpc, addresses, config);
  assertAccountsExist(maybeAccounts);
  return maybeAccounts;
}

export async function fetchAllMaybeTreeConfig(
  rpc: Parameters<typeof fetchEncodedAccounts>[0],
  addresses: Array<Address>,
  config?: FetchAccountsConfig
): Promise<MaybeAccount<TreeConfig>[]> {
  const maybeAccounts = await fetchEncodedAccounts(rpc, addresses, config);
  return maybeAccounts.map((maybeAccount) => decodeTreeConfig(maybeAccount));
}

export function getTreeConfigSize(): number {
  return 96;
}

export async function fetchTreeConfigFromSeeds(
  rpc: Parameters<typeof fetchEncodedAccount>[0],
  seeds: TreeConfigSeeds,
  config: FetchAccountConfig & { programAddress?: Address } = {}
): Promise<Account<TreeConfig>> {
  const maybeAccount = await fetchMaybeTreeConfigFromSeeds(rpc, seeds, config);
  assertAccountExists(maybeAccount);
  return maybeAccount;
}

export async function fetchMaybeTreeConfigFromSeeds(
  rpc: Parameters<typeof fetchEncodedAccount>[0],
  seeds: TreeConfigSeeds,
  config: FetchAccountConfig & { programAddress?: Address } = {}
): Promise<MaybeAccount<TreeConfig>> {
  const { programAddress, ...fetchConfig } = config;
  const [address] = await findTreeConfigPda(seeds, { programAddress });
  return await fetchMaybeTreeConfig(rpc, address, fetchConfig);
}
